if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          galleryCountCurrent: this.querySelector('.product__gallery-count-current'),
          galleryCountTotal: this.querySelector('.product__gallery-count-total'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.activeMediaSyncFrame = null;
        this.activeMediaSyncTimeout = null;
        this.activeMediaObserver = null;
        this.currentMediaId = null;
        this.initializeGalleryCount();
        this.ensureActiveMedia();
        this.scheduleEnsureActiveMedia();
        this.scheduleEnsureActiveMedia(120);
        this.scheduleEnsureActiveMedia(420);
        window.addEventListener(
          'load',
          () => {
            this.ensureActiveMedia();
            this.scheduleEnsureActiveMedia(120);
          },
          { once: true }
        );
        this.observeActiveMediaState();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const currentMediaId = event.detail.currentElement?.dataset?.mediaId;
        if (!currentMediaId) return;

        this.currentMediaId = currentMediaId;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentMediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateGalleryCount(thumbnail?.dataset.mediaPosition);
      }

      scheduleEnsureActiveMedia(delay = 0) {
        if (this.activeMediaSyncFrame) window.cancelAnimationFrame(this.activeMediaSyncFrame);
        if (this.activeMediaSyncTimeout) window.clearTimeout(this.activeMediaSyncTimeout);

        const runEnsure = () => {
          this.activeMediaSyncTimeout = null;
          this.activeMediaSyncFrame = window.requestAnimationFrame(() => {
            this.activeMediaSyncFrame = null;
            this.ensureActiveMedia();
          });
        };

        if (delay > 0) {
          this.activeMediaSyncTimeout = window.setTimeout(runEnsure, delay);
          return;
        }

        runEnsure();
      }

      observeActiveMediaState() {
        if (!this.elements.viewer || typeof MutationObserver === 'undefined') return;

        this.activeMediaObserver = new MutationObserver((mutations) => {
          const shouldResync = mutations.some((mutation) => {
            if (mutation.type === 'childList') return true;
            if (
              mutation.type === 'attributes' &&
              ['class', 'hidden', 'aria-hidden', 'aria-current'].includes(mutation.attributeName)
            )
              return true;
            return false;
          });

          if (!shouldResync) return;
          this.scheduleEnsureActiveMedia();
        });

        this.activeMediaObserver.observe(this.elements.viewer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'hidden', 'aria-hidden', 'aria-current'],
        });
      }

      ensureActiveMedia() {
        if (!this.elements.viewer) return;

        const activeViewerItem = this.elements.viewer.querySelector('[data-media-id].is-active');
        const currentThumbnailTarget = this.elements.thumbnails
          ?.querySelector('button[aria-current="true"]')
          ?.closest('[data-target]')
          ?.dataset?.target;
        const firstViewerItem = this.elements.viewer.querySelector('[data-media-id]');
        const resolvedMediaId =
          this.currentMediaId ||
          activeViewerItem?.dataset?.mediaId ||
          currentThumbnailTarget ||
          this.dataset.initialMediaId ||
          firstViewerItem?.dataset?.mediaId;

        if (!resolvedMediaId) return;

        const isResolvedAlreadyActive = activeViewerItem?.dataset?.mediaId === resolvedMediaId;

        if (isResolvedAlreadyActive) {
          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${resolvedMediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.updateGalleryCount(activeThumbnail?.dataset.mediaPosition);
          return;
        }

        this.setActiveMedia(resolvedMediaId, false);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        const isMobileLayout = !this.mql.matches;
        const shouldPrepend = Boolean(prepend) && !isMobileLayout;
        this.currentMediaId = activeMedia.dataset.mediaId;
        this.dataset.initialMediaId = activeMedia.dataset.mediaId;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
          if (isMobileLayout) {
            element.removeAttribute('hidden');
            element.setAttribute('aria-hidden', 'false');
          } else {
            element.setAttribute('aria-hidden', 'true');
            element.setAttribute('hidden', 'hidden');
          }
        });
        activeMedia?.classList?.add('is-active');
        activeMedia?.removeAttribute('hidden');
        activeMedia?.setAttribute('aria-hidden', 'false');

        if (shouldPrepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        } else if (this.elements.viewer.slider) {
          const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
          const activeIndex = Math.max(
            slides.findIndex((element) => element.dataset.mediaId === activeMedia.dataset.mediaId),
            0
          );
          const currentCounter = this.elements.viewer.querySelector('.slider-counter--current');
          if (currentCounter) currentCounter.textContent = `${activeIndex + 1}`;
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Keep mobile photos below the sticky header when changing media.
          const mobileHeader = !this.mql.matches && document.querySelector('sticky-header');
          const topOffset = mobileHeader ? mobileHeader.getBoundingClientRect().height + 16 : 0;
          if (activeMediaRect.top > topOffset - 0.5) return;
          const top = Math.max(0, activeMediaRect.top + window.scrollY - topOffset);
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.updateGalleryCount(activeThumbnail?.dataset.mediaPosition);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition || 1);
      }

      initializeGalleryCount() {
        if (!this.elements.galleryCountCurrent || !this.elements.galleryCountTotal) return;

        const currentThumbnail = this.querySelector('[id^="GalleryThumbnails"] [aria-current="true"]')?.closest(
          '[data-media-position]'
        );
        const total = this.querySelector('.product__gallery-count')?.dataset.galleryTotal;

        this.updateGalleryCount(currentThumbnail?.dataset.mediaPosition || 1);

        if (total) {
          this.elements.galleryCountTotal.textContent = this.formatGalleryCount(total);
        }
      }

      updateGalleryCount(position) {
        if (!this.elements.galleryCountCurrent) return;
        this.elements.galleryCountCurrent.textContent = this.formatGalleryCount(position || 1);
      }

      formatGalleryCount(value) {
        return `${value}`.padStart(2, '0');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      disconnectedCallback() {
        if (this.activeMediaSyncFrame) window.cancelAnimationFrame(this.activeMediaSyncFrame);
        if (this.activeMediaSyncTimeout) window.clearTimeout(this.activeMediaSyncTimeout);
        this.activeMediaObserver?.disconnect();
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
